import type { CofQuizPicked, CofQuizQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { CircleOfFifths } from '../scale/circle-of-fifths.js'
import { CofSignatureAnswer } from './cof-signature-answer.js'
import { CofSpellingAnswer } from './cof-spelling-answer.js'
import styles from './cof-quiz-question.module.css'

export interface CofQuizQuestionPanelProps {
  question: CofQuizQuestion
  scoreCorrect: number
  scoreAnswered: number
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: CofQuizPicked) => void
}

const QUESTIONS_PER_SESSION = 10

const buildPrompt = (q: CofQuizQuestion): string => {
  switch (q.kind) {
    case 'find-relative':
      return q.referenceKey.ring === 'major'
        ? `Find the relative minor of ${q.referenceKey.name} major`
        : `Find the relative major of ${q.referenceKey.name} minor`
    case 'find-closely-related':
      return `Find a key closely related to ${q.referenceKey.name} ${q.referenceKey.ring}`
    case 'find-parallel':
      return q.referenceKey.ring === 'major'
        ? `Find the parallel minor of ${q.referenceKey.name} major`
        : `Find the parallel major of ${q.referenceKey.name} minor`
    case 'count-signature':
      return `${q.referenceKey.name} ${q.referenceKey.ring} — how many ${q.promptDirection}s?`
    case 'spell-enharmonic':
      return `Spell ${q.referenceKey.name} ${q.referenceKey.ring} with ${q.promptDirection}s`
  }
}

/**
 * CoF Wheel v1.5 Quiz "asking" surface — routes by `question.kind` to one of
 * three answer surfaces: the existing wheel canvas for the three relationship
 * kinds, the signature row for count-signature, the spelling grid for
 * spell-enharmonic. The wheel is interactive iff `interactive=true`; the
 * feedback phase re-renders this same panel with `interactive=false`.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.4
 */
export const CofQuizQuestionPanel: FC<CofQuizQuestionPanelProps> = props => {
  const { question, scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <span className={styles.score}>
          {scoreCorrect} / {QUESTIONS_PER_SESSION}
        </span>
        <span>
          Q {Math.min(scoreAnswered + 1, QUESTIONS_PER_SESSION)} / {QUESTIONS_PER_SESSION}
        </span>
      </header>
      <p className={styles.prompt}>{buildPrompt(question)}</p>
      <Button variant="outlined" size="small" onClick={onHearIt} className={styles.hearBtn}>
        Hear it
      </Button>
      {(question.kind === 'find-relative' ||
        question.kind === 'find-closely-related' ||
        question.kind === 'find-parallel') && (
        <div className={styles.wheel}>
          <CircleOfFifths
            onKeySelect={interactive ? picked => onAnswer({ kind: 'key', ...picked }) : undefined}
          />
        </div>
      )}
      {question.kind === 'count-signature' && (
        <CofSignatureAnswer question={question} interactive={interactive} onAnswer={onAnswer} />
      )}
      {question.kind === 'spell-enharmonic' && (
        <CofSpellingAnswer question={question} interactive={interactive} onAnswer={onAnswer} />
      )}
    </div>
  )
}
